import React, { useState, useRef } from "react";
import { Grid, Box, Typography, LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import CustomizedBreadcrumbs from "../../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedButton from "../../../../components/Custom/CustomizedButton";
import ImporterDropzoneUI from "../../../../components/UI/ImporterDropzoneUI";
import SettingService from "../../../../services/Setting";
import AgGrid from "../../../../components/Table/AgGrid";

const keys = [];

const columns = ["sub_level_1", "sub_level_2", "sub_level_3"];

const ItemGroupImporterPage = () => {
  //keys need to be defined in order
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const gridRef = useRef();
  const { t } = useTranslation();
  const [formattedData, setFormattedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCreateError, setHasCreateError] = useState(false);
  const [createErrors, setCreateErrors] = useState([]);

  const columnDefs = [
    {
      field: "sub_level_1_name",
      headerName: "หมวดหมู่สินค้าหลัก 1",
      filter: "agTextColumnFilter",
    },
    {
      field: "sub_level_2_name",
      headerName: "หมวดหมู่สินค้าย่อย 2",
      filter: "agTextColumnFilter",
    },
    {
      field: "sub_level_3_name",
      headerName: "หมวดหมู่สินค้าย่อย 3",
      filter: "agTextColumnFilter",
    },
  ];

  const breadcrumbs = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.warehouseSetting"),
      to: "/setting/inventory",
    },
    {
      name: t("setting.inventory.category.index"),
      to: "/setting/inventory/category",
    },
    {
      name: "นำเข้าหมวดหมู่",
    },
  ];

  const formatCategoryData = (data) => {
    let missingCols;

    try {
      const dataCols = Object.keys(data?.[0]);
      missingCols = columns.filter((col) => !dataCols.includes(col));
      console.log(missingCols);

      if (missingCols.length > 0) {
        throw new Error("template");
      }

      const formattedData = data.map((category) => {
        const sub_level_1 = category.sub_level_1?.toString().trim();
        const sub_level_2 = category.sub_level_2?.toString().trim();
        const sub_level_3 = category.sub_level_3?.toString().trim();

        return {
          sub_level_1_document_id: sub_level_1,
          sub_level_1_name: sub_level_1,
          sub_level_2_document_id: sub_level_2
            ? `${sub_level_1}-${sub_level_2}`
            : undefined,
          sub_level_2_name: sub_level_2,
          sub_level_3_document_id: sub_level_3
            ? `${sub_level_1}-${sub_level_2}-${sub_level_3}`
            : undefined,
          sub_level_3_name: sub_level_3,
        };
      });

      return formattedData;
    } catch (err) {
      console.log(err);
      if (err.message === "template") {
        enqueueSnackbar(
          `Template ไม่ตรง ไม่พบคอลัม ${missingCols.join(", ")}`,
          {
            variant: "error",
          }
        );
      }
      return [];
    }
  };

  const importFormattedDocuments = async () => {
    setIsLoading(true);
    setCreateErrors([]);
    if (formattedData.length === 0) {
      return;
    }
    try {
      const data = await SettingService.postCreateItemGroupWithSubLevels({
        createManyInput: formattedData,
      });
      console.log(data);
      enqueueSnackbar("นำเข้าสำเร็จ", {
        variant: "success",
      });
      navigate("/setting/inventory/category");
    } catch (err) {
      console.log(err);
      setHasCreateError(true);
    }
    setIsLoading(false);
  };

  const renderCreateErrors = () => {
    if (createErrors.find((error) => error.type === "template_from_importer")) {
      enqueueSnackbar("Template ไม่ตรง", {
        variant: "error",
      });
    }
    const requiredFieldErrors = [
      ...new Set(
        createErrors
          .filter((error) => error.type === "required")
          .map((error) => error.field)
      ),
    ];
    if (requiredFieldErrors.length > 0) {
      enqueueSnackbar(
        `ไม่พบข้อมูลใน column\n${requiredFieldErrors.join(", ")}`,
        {
          variant: "error",
          style: { whiteSpace: "pre-line" },
        }
      );
    }
    return <></>;
  };

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ my: 2 }}>
        <Typography variant="h5">{"นำเข้าหมวดหมู่"}</Typography>
      </Box>
      <Box
        sx={{ mt: 1, mb: 2, display: "flex", justifyContent: "space-between" }}
      >
        <CustomizedButton
          title="ดาวน์โหลด Template"
          variant="outlined"
          onClick={() =>
            window.open("/static/item_group_import_template.xlsx", "_blank")
          }
        />
      </Box>
      <Grid container>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={4}>
          <ImporterDropzoneUI
            keys={keys}
            setImportedDoc={setFormattedData}
            dataFormatter={formatCategoryData}
            setLoading={setIsLoading}
            setErrors={setCreateErrors}
            setHasError={setHasCreateError}
            hasLabelHeader
          />
        </Grid>
      </Grid>
      <Grid container sx={{ width: "100%" }}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={4} mt={2}>
          <Box sx={{ display: "flex", alignItems: "center" }}></Box>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={4} mt={2}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <CustomizedButton
              title="นำเข้า"
              variant="contained"
              onClick={importFormattedDocuments}
              disabled={formattedData.length === 0 || isLoading}
            />
          </Box>
        </Grid>
      </Grid>

      {isLoading && (
        <Box sx={{ mt: 3, width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      {formattedData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <AgGrid
            ref={gridRef}
            columnDefs={columnDefs}
            height={649}
            rowData={formattedData}
            groupDefaultExpanded={1}
            disableFloatingFilter
          />
        </Box>
      )}
      {hasCreateError && renderCreateErrors()}
    </>
  );
};

export default ItemGroupImporterPage;
